import React, { useContext, useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Menu, MenuButton, MenuItem, MenuItems, Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import {
  ArrowLeftRight,
  BookOpen,
  ChevronDown,
  KeyRound,
  LogOut,
  Menu as MenuIcon,
  Plus,
  Search,
  X
} from "lucide-react";
import { AppWorkspaceContext } from "../stores/AppWorkspace";
import { APP_PAGES, AppPageType } from "../helpers/constants";
import { Badge } from "../../ui";

type NavLinkDef = {
  label: string;
  page: AppPageType;
  href: string;
  icon: React.ReactNode;
  accountOnly?: boolean;
};

const NAV_LINKS: NavLinkDef[] = [
  {
    // Swap history. Shown only when the server says this visitor may list it
    // (signed in, holding an API key) — everyone else gets "Find swap".
    label: "Swaps",
    page: APP_PAGES.SWAPS,
    href: "/?page=swaps",
    icon: <ArrowLeftRight size={16} />
  },
  {
    label: "New swap",
    page: APP_PAGES.NEW_SWAP,
    href: "/?page=new_swap",
    icon: <Plus size={16} />
  },
  {
    label: "Find swap",
    page: APP_PAGES.FIND_SWAP,
    href: "/?page=find_swap",
    icon: <Search size={16} />
  },
  {
    label: "API keys",
    page: APP_PAGES.API_TOKENS,
    href: "/?page=api_tokens",
    icon: <KeyRound size={16} />,
    accountOnly: true
  }
];

const AppHeader: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // canOpen covers both rules: accountOnly pages, and the swaps list needing an
  // API key. "Find swap" replaces "Swaps" for everyone else.
  const navLinks = NAV_LINKS.filter(
    (l) => (!l.accountOnly || !workspace.isGuest) && workspace.canOpen(l.page)
  ).filter((l) => !(l.page === APP_PAGES.FIND_SWAP && workspace.canListSwaps));

  const gotoPage = (page: AppPageType) => (e: React.MouseEvent) => {
    e.preventDefault();
    workspace.setPage(page);
  };

  const renderNavLink = (link: NavLinkDef) => {
    const active = workspace.openPage === link.page;
    return (
      <a
        key={link.page}
        href={link.href}
        onClick={gotoPage(link.page)}
        className={`site-nav-link ${active ? "site-nav-link-active" : ""}`}
      >
        {link.icon}
        {link.label}
      </a>
    );
  };

  const pricingLink = (
    <a href="/pricing" className="site-nav-link">
      Pricing
    </a>
  );

  const apiDocsLink = (
    <a href="/api_docs" className="site-nav-link">
      <BookOpen size={16} />
      API
    </a>
  );

  // API usage only means anything once you have a key, which means an account.
  const usageBadge = !workspace.isGuest && workspace.apiUsage ? (
    <Badge tone="info">
      {workspace.apiUsage.data.used_weight} / {workspace.apiUsage.data.weight_limit} wt
    </Badge>
  ) : null;

  const guestCta = (
    <a href="/login" className="site-nav-link">
      <KeyRound size={16} />
      Sign in for API keys
    </a>
  );

  const userMenu = (
    <Menu as="div" className="relative">
      <MenuButton className="site-nav-link max-w-[220px]">
        <span className="truncate">{workspace.email}</span>
        <ChevronDown size={14} />
      </MenuButton>
      <MenuItems
        anchor="bottom end"
        className="z-50 mt-1 w-56 rounded-lg border border-border bg-paper-elevated p-1 shadow-(--shadow-panel) focus:outline-none"
      >
        <MenuItem>
          <a
            href="/logout"
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-danger no-underline hover:bg-danger-soft data-focus:bg-danger-soft"
          >
            <LogOut size={16} />
            Log out
          </a>
        </MenuItem>
      </MenuItems>
    </Menu>
  );

  return (
    <header className="site-header">
      <div className="mx-auto flex h-16 max-w-5xl items-center justify-between gap-4 px-4 sm:px-6">
        <div className="flex min-w-0 items-center gap-6">
          <a href="/" className="site-brand">
            {/* Transparent mark — light header background only. */}
            <img
              src="/branding/lightning-swap-mark-v3-64x64.png"
              srcSet="/branding/lightning-swap-mark-v3-128x128.png 2x"
              alt=""
              aria-hidden="true"
              width={32}
              height={32}
              className="site-brand-logo"
            />
            Lightning Swap
          </a>
          {!isMobile && (
            <nav className="flex items-center gap-1">
              {navLinks.map(renderNavLink)}
              {workspace.isGuest && pricingLink}
              {apiDocsLink}
            </nav>
          )}
        </div>

        {isMobile ? (
          <div className="flex items-center gap-2">
            {usageBadge}
            <Disclosure as="div" className="relative">
              {({ open }) => (
                <>
                  <DisclosureButton
                    className="rounded-md border border-border bg-paper-elevated p-2 text-ink"
                    aria-label="Menu"
                  >
                    {open ? <X size={18} /> : <MenuIcon size={18} />}
                  </DisclosureButton>
                  <DisclosurePanel className="absolute right-0 z-50 mt-2 w-64 rounded-lg border border-border bg-paper-elevated p-2 shadow-(--shadow-panel)">
                    {!workspace.isGuest && (
                      <div className="truncate px-3 py-2 text-xs text-muted">{workspace.email}</div>
                    )}
                    {navLinks.map((link) => (
                      <a
                        key={link.page}
                        href={link.href}
                        onClick={gotoPage(link.page)}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-ink no-underline hover:bg-accent-soft/70"
                      >
                        {link.icon}
                        {link.label}
                      </a>
                    ))}
                    {workspace.isGuest && (
                      <a
                        href="/pricing"
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-ink no-underline hover:bg-accent-soft/70"
                      >
                        Pricing
                      </a>
                    )}
                    <a
                      href="/api_docs"
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-ink no-underline hover:bg-accent-soft/70"
                    >
                      <BookOpen size={16} />
                      API
                    </a>
                    <div className="my-2 border-t border-border" />
                    {workspace.isGuest ? (
                      <a
                        href="/login"
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-ink no-underline hover:bg-accent-soft/70"
                      >
                        <KeyRound size={16} />
                        Sign in for API keys
                      </a>
                    ) : (
                      <a
                        href="/logout"
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-danger no-underline hover:bg-danger-soft"
                      >
                        <LogOut size={16} />
                        Log out
                      </a>
                    )}
                  </DisclosurePanel>
                </>
              )}
            </Disclosure>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {usageBadge}
            {workspace.isGuest ? guestCta : userMenu}
          </div>
        )}
      </div>
    </header>
  );
});

export default AppHeader;
